import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, GlobeAltIcon, CurrencyDollarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const StatsSection = () => {
  const [counters, setCounters] = useState({
    clients: 0,
    countries: 0,
    aum: 0,
    transactions: 0
  });

  const stats = [
    {
      icon: UsersIcon,
      key: 'clients',
      end: 250000,
      suffix: '+',
      label: "Active Clients",
      description: "Trust us with their investments",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: GlobeAltIcon,
      key: 'countries',
      end: 150,
      suffix: '+',
      label: "Countries Served",
      description: "Global reach and presence",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: CurrencyDollarIcon,
      key: 'aum',
      end: 5.2,
      suffix: 'B+',
      label: "Assets Managed",
      description: "Under professional management",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: ShieldCheckIcon,
      key: 'transactions',
      end: 10,
      suffix: 'M+',
      label: "Secure Transactions",
      description: "Processed annually",
      color: "from-amber-500 to-orange-600"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
          }
        });
      },
      { threshold: 0.2 }
    );

    const section = document.getElementById('stats-section');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const animateCounters = () => {
    stats.forEach((stat) => {
      let start = 0;
      const end = stat.end;
      const duration = 2000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCounters((prev) => ({ ...prev, [stat.key]: end }));
          clearInterval(timer);
        } else {
          setCounters((prev) => ({ ...prev, [stat.key]: start }));
        }
      }, 16);
    });
  };

  const formatNumber = (num, decimals = 0) => {
    return num.toFixed(decimals);
  };

  return (
    <section id="stats-section" className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-amber-50 border border-amber-200 mb-6"
          >
            <span className="text-sm font-semibold text-amber-700">Trusted Worldwide</span>
          </motion.div>

          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
            Numbers That Speak for Themselves
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of investors who have transformed their financial future with Blockfirst Capital
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            const value = counters[stat.key];
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group relative"
              >
                <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
                  {/* Gradient Background */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stat.color} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} p-0.5 mb-6`}
                  >
                    <div className="w-full h-full bg-white rounded-2xl flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-amber-600" strokeWidth={2} />
                    </div>
                  </motion.div>

                  {/* Value */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 + 0.5 }}
                    viewport={{ once: true }}
                    className="mb-3"
                  >
                    <h3 className="text-4xl lg:text-5xl font-bold text-gray-900">
                      {stat.key === 'aum' ? '$' : ''}
                      {formatNumber(value, stat.key === 'aum' ? 1 : 0)}
                      {stat.suffix}
                    </h3>
                  </motion.div>

                  {/* Label */}
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    {stat.label}
                  </p>

                  {/* Description */}
                  <p className="text-sm text-gray-600">
                    {stat.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-sm text-gray-500 mb-6">Trusted by leading organizations worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-40">
            <div className="text-2xl font-bold text-gray-400">Bloomberg</div>
            <div className="text-2xl font-bold text-gray-400">Reuters</div>
            <div className="text-2xl font-bold text-gray-400">Forbes</div>
            <div className="text-2xl font-bold text-gray-400">WSJ</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;
